import type { ReactNode } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import every from "lodash/every";
import ChatSQLCard from "./ChatSQLCard";
import ChatProtocolCard from "./ChatProtocolCard";
import ChatPlotCard from "./ChatPlotCard";
import { formatSelectedSources } from "../../utils/selectedSourcesFormatter";
import { filterDisplayableAgentSources } from "../../utils/chatSourceFilters";
import type { ChatMessage } from "./chat";

type ChatSourcesProps = {
  sources: ChatMessage;
  handleCloseSearchSources: () => void;
  hideHeader?: boolean;
  contextDetail?: ReactNode;
  compact?: boolean;
};

const isEmptyArray = (array: unknown) =>
  !isArray(array) || isEmpty(array) || every(array, isEmpty);

export default function ChatSources({
  handleCloseSearchSources,
  sources,
  hideHeader = false,
  contextDetail,
  compact = false,
}: ChatSourcesProps) {
  const sqlAgentData = filterDisplayableAgentSources(
    sources?.selected_sources?.SQLAgentSchema,
  );
  const protocolData = filterDisplayableAgentSources(
    sources?.selected_sources?.ProtocolAgentSchema,
  );
  const plotData = filterDisplayableAgentSources(
    sources?.selected_sources?.PlotAgentSchema,
  );
  const rawDataViews =
    sqlAgentData.length > 0
      ? sqlAgentData
      : sources?.selected_sources?.vivo_data_views;
  const dataViews =
    rawDataViews && !Array.isArray(rawDataViews)
      ? [rawDataViews]
      : rawDataViews;

  const formattedSources = formatSelectedSources({
    ...sources,
    selected_sources: {
      ...sources?.selected_sources,
      SQLAgentSchema: sqlAgentData,
      ProtocolAgentSchema: protocolData,
      PlotAgentSchema: plotData,
    },
  });

  const hasSQLData = dataViews && !isEmptyArray(dataViews);
  const hasProtocolData = protocolData && !isEmptyArray(protocolData);
  const hasPlotData = plotData && !isEmptyArray(plotData);
  const hasAnyData = hasSQLData || hasProtocolData || hasPlotData;

  return (
    <div
      className={
        compact
          ? "flex h-full min-h-0 flex-col"
          : "flex flex-col mt-[10px] mr-0 ml-0"
      }
    >
      {!hideHeader && (
        <div className="flex align-middle items-center justify-between py-2 separator">
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-[#556C75] font-semibold ml-2">
              Explanation
            </p>
          </div>
          <Button
            className="p-0 m-0"
            type="text"
            onClick={handleCloseSearchSources}
            icon={<CloseOutlined />}
            data-testid="explanation-panel-close"
          />
        </div>
      )}
      <div
        className={
          compact
            ? "flex-1 min-h-0 overflow-y-auto hide-scrollbar"
            : "w-full pt-3 px-2"
        }
      >
        {contextDetail}
        {hasAnyData ? (
          <>
            {hasSQLData && <ChatSQLCard sources={formattedSources} />}
            {hasPlotData && (
              <div className={hasSQLData ? "mt-4" : ""}>
                <ChatPlotCard sources={plotData} />
              </div>
            )}
            {hasProtocolData && (
              <div className={hasSQLData ? "mt-4" : ""}>
                <ChatProtocolCard sources={protocolData} />
              </div>
            )}
          </>
        ) : (
          <p className="text-[#00000073] text-sm">
            Ask Vivo did not provide an explanation.
          </p>
        )}
      </div>
    </div>
  );
}
